import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtINR } from "@/lib/format";
import { Plus, Trash2, TrendingUp } from "lucide-react";

const MONTH_LABELS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const currentFY = () => {
  const d = new Date(); const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String(y + 1).slice(-2)}`;
};
const FYS = () => {
  const cy = new Date().getFullYear();
  return [cy - 2, cy - 1, cy, cy + 1].map((y) => `${y}-${String(y + 1).slice(-2)}`);
};

function Sparkline({ actuals, budget }) {
  const max = Math.max(budget, ...actuals) || 1;
  return (
    <div className="flex items-end gap-0.5 h-10">
      {actuals.map((a, i) => (
        <div key={i} className="w-2 rounded-sm bg-accent/70" style={{ height: `${Math.max(2, (Math.abs(a) / max) * 40)}px` }} title={`${MONTH_LABELS[i]}: ${fmtINR(a)}`} />
      ))}
    </div>
  );
}

export default function Budgets() {
  const [fy, setFy] = useState(currentFY());
  const [accounts, setAccounts] = useState([]);
  const [ccs, setCCs] = useState([]);
  const [bva, setBva] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", account_id: "", cost_centre_id: "",
    month_amounts: Array(12).fill(0), notes: "",
  });

  const load = async () => {
    const [b, f] = await Promise.all([
      api.get("/reports/budget-vs-actual", { params: { fy } }),
      api.get("/reports/budget-forecast", { params: { fy } }),
    ]);
    setBva(b.data); setForecast(f.data);
  };
  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data));
    api.get("/dimensions").then((r) => setCCs(r.data));
  }, []);
  useEffect(() => { load(); }, [fy]);

  const setMonth = (i, v) => setForm((f) => ({ ...f, month_amounts: f.month_amounts.map((x, j) => j === i ? Number(v || 0) : x) }));
  const total = useMemo(() => form.month_amounts.reduce((s, x) => s + Number(x || 0), 0), [form.month_amounts]);

  const create = async () => {
    if (!form.account_id) return toast.error("Pick an account");
    try {
      await api.post("/budgets", {
        ...form, fiscal_year: fy,
        cost_centre_id: form.cost_centre_id || null,
      });
      toast.success("Budget saved");
      setOpen(false);
      setForm({ name: "", account_id: "", cost_centre_id: "", month_amounts: Array(12).fill(0), notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this budget?")) return;
    try { await api.delete(`/budgets/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const distribute = (annualAmt) => {
    const per = Math.round((Number(annualAmt) || 0) / 12);
    setForm((f) => ({ ...f, month_amounts: Array(12).fill(per) }));
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="budgets-page">
      <PageHeader eyebrow="Planning" title="Budgeting &amp; Forecasting" description="Set monthly budgets by account · cost-centre · reporting-tag. Compare vs actuals and forecast the FY."
        actions={
          <>
            <Select value={fy} onValueChange={setFy}>
              <SelectTrigger className="h-9 w-32 rounded-sm" data-testid="fy-select"><SelectValue /></SelectTrigger>
              <SelectContent>{FYS().map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => setOpen(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-budget-btn"><Plus className="w-3.5 h-3.5" /> New budget</Button>
          </>
        } />

      <Tabs defaultValue="bva">
        <TabsList className="rounded-sm">
          <TabsTrigger value="bva" className="rounded-sm">Budget vs Actuals</TabsTrigger>
          <TabsTrigger value="fc" className="rounded-sm">FY Forecast</TabsTrigger>
        </TabsList>

        <TabsContent value="bva" className="mt-4 space-y-4">
          {bva && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Total budgeted</div><div className="metric-value text-2xl mt-2">{fmtINR(bva.total_budget)}</div></div>
                <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Total actual</div><div className="metric-value text-2xl mt-2">{fmtINR(bva.total_actual)}</div></div>
                <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Variance</div><div className={`metric-value text-2xl mt-2 ${bva.total_variance >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtINR(bva.total_variance)}</div></div>
              </div>
              <div className="card-sharp overflow-hidden">
                <table className="w-full dense">
                  <thead><tr><th>Budget</th><th>Account</th><th>CC / Tag</th><th className="text-right">Budget</th><th className="text-right">Actual</th><th className="text-right">Variance</th><th className="text-right">%</th><th>Trend</th><th></th></tr></thead>
                  <tbody>
                    {bva.rows.length === 0 && <tr><td colSpan="9" className="text-center py-8 text-muted-foreground">No budgets set for FY {bva.fiscal_year}. Click "New budget".</td></tr>}
                    {bva.rows.map((r) => (
                      <tr key={r.id} data-testid={`bva-row-${r.id}`}>
                        <td className="text-sm font-medium">{r.name}</td>
                        <td className="text-xs"><span className="font-mono text-muted-foreground">{r.account_code}</span> {r.account_name}</td>
                        <td className="text-xs text-muted-foreground">{r.cost_centre_name || "—"}</td>
                        <td className="text-right tabular-nums">{fmtINR(r.total)}</td>
                        <td className="text-right tabular-nums">{fmtINR(r.actual_total)}</td>
                        <td className={`text-right tabular-nums font-medium ${r.variance >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtINR(r.variance)}</td>
                        <td className={`text-right text-xs font-mono ${r.variance_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>{r.variance_pct}%</td>
                        <td><Sparkline actuals={r.actuals} budget={r.total / 12} /></td>
                        <td className="text-right"><button className="text-muted-foreground hover:text-destructive" onClick={() => remove(r.id)} data-testid={`del-budget-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="fc" className="mt-4">
          {forecast && (
            <div className="card-sharp overflow-hidden">
              <div className="p-4 border-b flex items-center gap-2 text-sm text-muted-foreground">
                <TrendingUp className="w-4 h-4 text-accent" />
                Projection based on YTD run-rate · {forecast.elapsed_months || 0} of 12 months elapsed
              </div>
              <table className="w-full dense">
                <thead><tr><th>Budget</th><th className="text-right">Budgeted</th><th className="text-right">YTD Actual</th><th className="text-right">Run rate / mo</th><th className="text-right">Projected FY</th><th className="text-right">Projected variance</th></tr></thead>
                <tbody>
                  {forecast.rows.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">No budgets to project</td></tr>}
                  {forecast.rows.map((r) => (
                    <tr key={r.id}>
                      <td className="text-sm">{r.name}</td>
                      <td className="text-right tabular-nums">{fmtINR(r.budget_total)}</td>
                      <td className="text-right tabular-nums">{fmtINR(r.ytd_actual)}</td>
                      <td className="text-right tabular-nums text-muted-foreground">{fmtINR(r.monthly_run_rate)}</td>
                      <td className="text-right tabular-nums font-medium">{fmtINR(r.projected_fy_total)}</td>
                      <td className={`text-right tabular-nums ${r.projected_variance >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtINR(r.projected_variance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none overflow-y-auto space-y-4">
          <SheetHeader><SheetTitle className="font-heading">New budget · FY {fy}</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" placeholder="e.g. Marketing spend" data-testid="budget-name" /></div>
            <div>
              <Label>Account</Label>
              <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="budget-account"><SelectValue placeholder="Pick GL account" /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</div></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dimension (optional)</Label>
              <Select value={form.cost_centre_id || "none"} onValueChange={(v) => setForm({ ...form, cost_centre_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">All</SelectItem>{ccs.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Monthly amounts</div>
              <div className="flex items-center gap-2">
                <Input type="number" placeholder="Annual → distribute" onBlur={(e) => e.target.value && distribute(e.target.value)} className="rounded-sm h-8 w-40 text-xs" data-testid="budget-annual-distribute" />
                <div className="text-xs">Total: <span className="font-mono font-medium">{fmtINR(total)}</span></div>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {MONTH_LABELS.map((m, i) => (
                <div key={m}>
                  <Label className="text-[10px] font-mono uppercase text-muted-foreground">{m}</Label>
                  <Input type="number" value={form.month_amounts[i]} onChange={(e) => setMonth(i, e.target.value)} className="rounded-sm h-8 text-right tabular-nums" data-testid={`budget-month-${i}`} />
                </div>
              ))}
            </div>
          </div>

          <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1" /></div>

          <Button onClick={create} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="save-budget-btn">Save budget</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
